// AdminPanel/HeaderFinder — the Search tab's "Header" lookup: given a sender id
// off an SMS, find every customer sending under it.
//
// Backed by ListOrganizationsV3 (GET /api/v3/organizations?search=), which
// matches headers through an EXISTS on sms_sender_ids. EXISTS rather than a
// join, because a tenant has several headers and a join would multiply its row,
// breaking both the page size and the count.
//
// Unlike the Org ID and API Key tabs this is deliberately MANY-to-one: EDUMRC
// is the shared default across hundreds of tenants, so a result list is the
// normal case, not an error.
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Search} from '@mui/icons-material';
import {
  Alert, Box, Button, CircularProgress, TextField, Typography
} from '@mui/material';

import {adminLookupOrgs} from '../../actions/adminPanel';
import {validateHeader} from './orgSearch';
import OrgResultCard from './OrgResultCard';

class HeaderFinder extends Component {
  state = {query: '', searching: false, results: null, error: null};

  // Guards against a stale response landing after a newer search (or
  // unmount) — only the latest request may write state.
  searchSeq = 0;

  componentWillUnmount() {
    this.searchSeq++;
  }

  search = async () => {
    const check = validateHeader(this.state.query);
    if (!check.ok) {
      this.setState({error: check.error, results: null});
      return;
    }

    const seq = ++this.searchSeq;
    this.setState({searching: true, results: null, error: null});
    try {
      const orgs = await adminLookupOrgs(check.value);
      if (this.searchSeq !== seq) return;
      this.setState({searching: false, results: orgs});
    } catch (err) {
      if (this.searchSeq !== seq) return;
      const status = err?.response?.status;
      const msg = status === 403
        ? 'You don\'t have permission to use admin search.'
        : err?.response?.data?.message || err?.message || 'Search failed — please try again.';
      this.setState({searching: false, error: msg});
    }
  };

  render() {
    const {query, searching, error, results} = this.state;
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
          Find every customer sending under a header. Matching is partial, so
          &quot;EDU&quot; returns any header containing it. A shared header such as
          EDUMRC belongs to many tenants — expect a list.
        </Typography>
        <Box sx={{display: 'flex', gap: 1, mb: 2}}>
          <TextField
            fullWidth
            size="small"
            label="Header (sender id)"
            placeholder="EDUMRC"
            value={query}
            onChange={(e) => this.setState({query: e.target.value, error: null})}
            onKeyDown={(e) => { if (e.key === 'Enter') this.search(); }}
            disabled={searching}
          />
          <Button
            variant="contained"
            onClick={this.search}
            disabled={searching}
            startIcon={searching ? <CircularProgress size={16} color="inherit" /> : <Search />}
            sx={{flexShrink: 0}}
          >
            Search
          </Button>
        </Box>
        {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
        {results && results.length === 0 && (
          <Alert severity="info">
            No customer uses a header matching that. Headers are stored upper-case; try a shorter fragment.
          </Alert>
        )}
        {results && results.map((o) => (
          <OrgResultCard key={o.id} org={o} orgs={this.props.orgs} onOpenOrg={this.props.onOpenOrg} />
        ))}
      </Box>
    );
  }
}

HeaderFinder.propTypes = {
  orgs: PropTypes.array,
  onOpenOrg: PropTypes.func.isRequired
};

export default HeaderFinder;
